import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
} from "@chakra-ui/react";
import {
  ImportParamBase,
  SyncImportParamsBase,
} from "@syla/shared/types/models/DataSourceBase";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import { sortBy } from "lodash";
import React, { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { MdLockOutline } from "react-icons/md";

export type SyncApiFormFields = {
  apiKey: string;
  secretKey?: string;
  passphrase?: string;
};

export function SyncApiFields({
  dataSource,
}: {
  dataSource: DataSourceWithNetworks;
}): React.ReactElement {
  const form = useFormContext<SyncApiFormFields>();

  const inputs = useMemo(() => {
    if (!dataSource.syncImportParams) {
      // shouldn't happen, as we never show this panel if there are no sync params
      return [];
    }

    const mapped = Object.entries(dataSource.syncImportParams)
      .filter(([key]) =>
        Object.values(SyncParamKey).includes(key as SyncParamKey),
      )
      .map(([key, value]) => ({
        key: key as SyncParamKey,
        formField: mapSyncParamKeyToFormField[key as SyncParamKey],
        ...(value as ImportParamBase),
      }));

    return sortBy(mapped, (param) => param.order);
  }, [dataSource.syncImportParams]);

  return (
    <>
      {inputs.map((param, index) => {
        const showLock = param.key !== "key";
        const error = form.formState.errors[param.formField];
        return (
          <FormControl
            key={param.key}
            isDisabled={form.formState.isSubmitting}
            isInvalid={!!error}
            isRequired
          >
            <FormLabel display="flex" alignItems="center" gap={1}>
              {showLock && <Icon boxSize={4} as={MdLockOutline} />}
              {param.text}
            </FormLabel>
            <Input
              {...form.register(param.formField, {
                required: `${param.text} is required`,
              })}
              placeholder={`Enter ${param.text} here`}
              autoFocus={index === 0}
            />
            <FormErrorMessage>{error?.message}</FormErrorMessage>
          </FormControl>
        );
      })}
    </>
  );
}

enum SyncParamKey {
  Key = "key",
  Secret = "secret",
  Passphrase = "passphrase",
}

const mapSyncParamKeyToFormField: Record<
  keyof SyncImportParamsBase,
  keyof SyncApiFormFields
> = {
  [SyncParamKey.Key]: "apiKey",
  [SyncParamKey.Secret]: "secretKey",
  [SyncParamKey.Passphrase]: "passphrase",
};
